import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PlayCircle } from "lucide-react";
import api from "@/lib/api";
import useSeo from "@/hooks/useSeo";
import { formatDate } from "@/lib/constants";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const TABS = [
  { key: "todos", label: "Todos" },
  { key: "Videos", label: "Videos" },
  { key: "Entrevistas", label: "Entrevistas" },
  { key: "Podcast", label: "Podcast" },
  { key: "Documentales", label: "Documentales" },
  { key: "Galerías", label: "Galerías" },
  { key: "Transmisiones", label: "Transmisiones" },
];

function MediaCard({ a }) {
  return (
    <Link to={`/multimedia/${a.slug}`} data-testid={`media-${a.slug}`} className="group">
      <div className="relative overflow-hidden rounded-md aspect-video mb-3">
        <img src={a.image} alt={a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/15 transition-colors flex items-center justify-center">
          <PlayCircle size={48} className="text-white/90" strokeWidth={1.2} />
        </div>
        <span className="absolute top-3 left-3 text-[10px] font-bold tracking-widest uppercase bg-nx-orange text-white px-2 py-1">{a.subcategory}</span>
      </div>
      <h3 className="font-serif font-bold text-lg leading-snug text-nx-dark group-hover:text-nx-blue transition-colors">{a.title}</h3>
      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{a.subtitle}</p>
      <p className="text-xs text-gray-500 mt-2">{formatDate(a.date)}</p>
    </Link>
  );
}

export default function Multimedia() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("todos");
  useSeo({ title: "Multimedia", description: "Videos, entrevistas, podcast, documentales, galerías y transmisiones de NEXODUM." });

  useEffect(() => { api.get("/articles?category=multimedia&limit=40").then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);

  const filtered = tab === "todos" ? items : items.filter((a) => a.subcategory === tab);

  return (
    <div data-testid="multimedia-page">
      <section className="bg-nx-dark text-white">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="flex items-center gap-3">
            <span className="w-2 h-10 rounded-sm bg-nx-orange" />
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] text-nx-orange">NEXODUM</p>
              <h1 className="font-serif font-black text-4xl md:text-6xl tracking-tight">Multimedia</h1>
            </div>
          </div>
          <p className="mt-4 text-slate-300 max-w-2xl text-sm md:text-base">Videos, entrevistas, podcasts, documentales, galerías y transmisiones. Espacio preparado para integrar videos de YouTube.</p>
        </div>
      </section>

      <section className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex flex-wrap h-auto bg-transparent gap-2 mb-8 justify-start">
            {TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`media-tab-${t.key}`}
                className="data-[state=active]:bg-nx-dark data-[state=active]:text-white rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider border border-gray-200">
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value={tab}>
            {filtered.length === 0 ? (
              <p className="text-center text-gray-500 py-16">No hay contenidos en esta categoría todavía.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {filtered.map((a) => <MediaCard key={a.id} a={a} />)}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
}
